// ==========================================================
// FILE: src/app/core/localdata/core-sqlite-services/productos/product-price-local.service.ts
// ==========================================================
import { Injectable } from '@angular/core';
import { SqliteConexionService } from '../sqlite-conexion.service';
import { PendingCoreService } from '../../pending/pending-core.service';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { DeviceCoreService } from '../../../system/device-core.service';

export interface ProductPriceLocal {
  rowid?: number;
  backend_id?: number;
  fk_product?: number;         // ID del producto backend (después de sincronizar)
  fk_product_local?: number;   // rowid del producto local
  price_level?: number;
  price: number;
  price_base_type?: string;
  tms?: string;
  datec?: string;
  start_date?: string;
  end_date?: string;
  status?: number;
  synced?: number;
  userId?: string;
  deviceToken?: string;
}

@Injectable({ providedIn: 'root' })
export class ProductPriceLocalService {
  private table = 'llx_product_price';

  constructor(
    private sqliteService: SqliteConexionService,
    private pendingService: PendingCoreService,
    private authService: AuthService,
    private deviceService: DeviceCoreService
  ) {}

  private async getUserAndDevice(): Promise<{ userId: string; deviceToken: string }> {
    const device = await this.deviceService.getDeviceInfo();
    const user = await this.authService.getCurrentUserAsync();
    if (!user) throw new Error('No hay usuario logueado para PendingOps');
    return { userId: user.login, deviceToken: device.deviceToken };
  }

  async getAllPrices(): Promise<ProductPriceLocal[]> {
    return await this.sqliteService.query(`SELECT * FROM ${this.table} WHERE status != 2 ORDER BY rowid`);
  }

  async getPricesByProduct(fk_product: number, fk_product_local?: number): Promise<ProductPriceLocal[]> {
    return await this.sqliteService.query(
      `SELECT * FROM ${this.table} WHERE fk_product = ? OR fk_product_local = ? ORDER BY rowid`,
      [fk_product, fk_product_local ?? fk_product]
    );
  }

  async addPrice(price: ProductPriceLocal, localProductRowid?: number): Promise<number> {
    price.status = price.status ?? 1;
    price.synced = 0;
    const { userId, deviceToken } = await this.getUserAndDevice();
    price.userId = userId;
    price.deviceToken = deviceToken;

    // ✅ Siempre asigna fk_product_local para inserts offline
    if (localProductRowid) price.fk_product_local = localProductRowid;
    if (!price.fk_product) price.fk_product = null;

    const insertable = { ...price };
    delete insertable.rowid;

    const fields = Object.keys(insertable).join(', ');
    const placeholders = Object.keys(insertable).map(() => '?').join(', ');
    const values = Object.values(insertable);

    await this.sqliteService.execute(`INSERT INTO ${this.table} (${fields}) VALUES (${placeholders})`, values);

    const res = await this.sqliteService.query('SELECT last_insert_rowid() AS id');
    price.rowid = res[0].id;

    await this.pendingService.addOp({ table: this.table, action: 'INSERT', payload: price });
    return price.rowid!;
  }

  async updatePrice(price: ProductPriceLocal): Promise<void> {
    if (!price.rowid) throw new Error('rowid es requerido');
    const { userId, deviceToken } = await this.getUserAndDevice();
    price.userId = userId;
    price.deviceToken = deviceToken;

    const updates = Object.entries(price)
      .filter(([key]) => key !== 'rowid')
      .map(([key]) => `${key} = ?`)
      .join(', ');

    const values = Object.entries(price)
      .filter(([key]) => key !== 'rowid')
      .map(([_, value]) => value);

    await this.sqliteService.execute(`UPDATE ${this.table} SET ${updates} WHERE rowid = ?`, [...values, price.rowid]);
    await this.pendingService.addOp({ table: this.table, action: 'UPDATE', payload: price });
  }

  async deletePrice(rowid: number): Promise<void> {
    const { userId, deviceToken } = await this.getUserAndDevice();
    await this.sqliteService.execute(`UPDATE ${this.table} SET status = 2, synced = 0 WHERE rowid = ?`, [rowid]);
    await this.pendingService.addOp({ table: this.table, action: 'DELETE', payload: { rowid, userId, deviceToken }, deleted: 1 });
  }

  async reassignPricesToBackendProduct(localProductId: number, backendProductId: number): Promise<void> {
    await this.sqliteService.execute(
      `UPDATE ${this.table} SET fk_product = ?, synced = 0 WHERE fk_product_local = ?`,
      [backendProductId, localProductId]
    );
  }

  async updatePriceBackendInfo(localPriceRowid: number, backendPriceId: number, backendFkProductId?: number): Promise<void> {
    await this.sqliteService.execute(
      `UPDATE ${this.table} SET backend_id = ?, fk_product = ?, synced = 1 WHERE rowid = ?`,
      [backendPriceId, backendFkProductId ?? null, localPriceRowid]
    );
  }

  async updateBackendId(localId: number, backendId: number): Promise<void> {
    await this.sqliteService.execute(`UPDATE ${this.table} SET backend_id = ?, synced = 1 WHERE rowid = ?`, [backendId, localId]);
  }
}
